import React, { useState, useEffect } from 'react';
import { Rnd } from 'react-rnd';               // ← replaces react-draggable
import './addtable.scss';
import Navbar from '../../components/navbar/Navbar';
import Sidebar from '../../components/sidebar/Sidebar';
import { jwtDecode } from 'jwt-decode';

const Addtable = () => {
  const [formData, setFormData] = useState({
    tableno: '',
    status: 'available',
    shape: 'circle',
    x: 0,
    y: 0,
    width: 150,
    height: 150,
  });

  const [existingTables, setExistingTables] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;

  // Load existing tables
  useEffect(() => {
    const fetchTables = async () => {
      try {
        const storedToken = localStorage.getItem('accessToken');
        const decoded = jwtDecode(storedToken);
        const merchantId = decoded.merchantId;

        const res = await fetch(`${apiUrl}/tables?merchantId=${merchantId}`, {
          headers: { Authorization: `Bearer ${storedToken}` },
        });
        const data = await res.json();
        setExistingTables(data);
      } catch (error) {
        console.error('Error fetching existing tables:', error);
      }
    };

    fetchTables();
  }, [apiUrl]);

  // Auto‑clear messages
  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setErrorMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]:
        name === 'x' || name === 'y' || name === 'width' || name === 'height'
          ? Number(value)
          : value,
    });
  };

  const validateForm = () => {
    if (!formData.tableno.trim()) {
      setErrorMessage('Table number is required');
      return false;
    }
    if (formData.width <= 0 || formData.height <= 0) {
      setErrorMessage('Width and height must be positive');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    if (!validateForm()) return;
    setLoading(true);

    try {
      const storedToken = localStorage.getItem('accessToken');
      if (!storedToken) throw new Error('Access token not found');

      const decodedToken = jwtDecode(storedToken);
      const merchantId = decodedToken.merchantId;
      if (!merchantId) throw new Error('Invalid token, Merchant ID not found');

      const payload = { ...formData, merchantId };

      const response = await fetch(`${apiUrl}/tables`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        setSuccessMessage('Table created successfully!');
        setFormData({
          tableno: '',
          status: 'available',
          shape: 'circle',
          x: 0,
          y: 0,
          width: 150,
          height: 150,
        });
        setExistingTables((prev) => [...prev, data]);
      } else {
        const err = await response.json();
        setErrorMessage(err.error || 'Unknown error');
      }
    } catch (error) {
      setErrorMessage(error.message);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle drag & resize from Rnd
  const handleDragStop = (e, d) => {
    setFormData((prev) => ({ ...prev, x: d.x, y: d.y }));
  };

  const handleResizeStop = (e, direction, ref, delta, position) => {
    setFormData({
      ...formData,
      width: parseInt(ref.style.width),
      height: parseInt(ref.style.height),
      x: position.x,
      y: position.y,
    });
  };

  return (
    <div className="addtable">
      <Sidebar />
      <div className="newContainer">
        <Navbar />
        <div className="top">
          <h1>Add New Table</h1>
        </div>
        <div className="bottom">
          <div className="right">
            {successMessage && <p className="success-message">{successMessage}</p>}
            {errorMessage && <p className="error-message">{errorMessage}</p>}

            <form onSubmit={handleSubmit}>
              <div className="formInput">
                <label>Table No *</label>
                <input
                  type="text"
                  name="tableno"
                  value={formData.tableno}
                  onChange={handleChange}
                  placeholder="T1"
                  required
                />
              </div>

              <div className="formInput">
                <label>Shape</label>
                <select name="shape" value={formData.shape} onChange={handleChange}>
                  <option value="circle">Circle</option>
                  <option value="rectangle">Rectangle</option>
                </select>
              </div>

              <div className="formInput">
                <label>Width *</label>
                <input
                  type="number"
                  name="width"
                  value={formData.width}
                  onChange={handleChange}
                  min="1"
                  required
                />
              </div>

              <div className="formInput">
                <label>Height *</label>
                <input
                  type="number"
                  name="height"
                  value={formData.height}
                  onChange={handleChange}
                  min="1"
                  required
                />
              </div>

              <button type="submit" disabled={loading}>
                {loading ? 'Adding...' : 'Add Table'}
              </button>
            </form>

            {/* Interactive floor plan */}
            <div
              className="floor-plan"
              style={{
                position: 'relative',
                width: '100%',
                height: '700px',            // you can adjust this
                border: '2px solid #ccc',
                marginTop: '20px',
                background: '#f0f0f0',
                backgroundImage: `
                  linear-gradient(to right, #ddd 1px, transparent 1px),
                  linear-gradient(to bottom, #ddd 1px, transparent 1px)
                `,
                backgroundSize: '20px 20px',
                overflow: 'auto',            // scroll if tables go outside
              }}
            >
              {/* Existing tables (static, non‑interactive) */}
              {existingTables.map((table) => (
                <div
                  key={table._id}
                  style={{
                    position: 'absolute',
                    left: table.x,
                    top: table.y,
                    width: table.width,
                    height: table.height,
                    borderRadius: table.shape === 'circle' ? '50%' : '4px',
                    backgroundColor: '#51910c',
                    opacity: 0.4,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',    // cannot drag or resize existing tables here
                    border: '1px solid rgba(0,0,0,0.2)',
                  }}
                >
                  {table.tableno}
                </div>
              ))}

              {/* New draggable & resizable table */}
              <Rnd
                size={{ width: formData.width, height: formData.height }}
                position={{ x: formData.x, y: formData.y }}
                onDragStop={handleDragStop}
                onResizeStop={handleResizeStop}
                bounds="parent"                // keeps table inside the floor plan
                enableResizing={{
                  top: true, right: true, bottom: true, left: true,
                  topRight: true, bottomRight: true, bottomLeft: true, topLeft: true,
                }}
                resizeHandleStyles={{
                  top: { cursor: 'ns-resize' },
                  right: { cursor: 'ew-resize' },
                  bottom: { cursor: 'ns-resize' },
                  left: { cursor: 'ew-resize' },
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: formData.shape === 'circle' ? '50%' : '4px',
                    backgroundColor: '#ff9800',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'move',
                    userSelect: 'none',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  }}
                >
                  {formData.tableno || 'New Table'}
                </div>
              </Rnd>
            </div>

            <p style={{ marginTop: '8px', fontSize: '0.9rem', color: '#555' }}>
              Drag to move · Drag corners/edges to resize
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Addtable;